/** Live ADGS deploy-gate queue, classified by the production selector. */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { AdgsQueueTicket } from '@a7y/types';
import { api } from '@/lib/api';
import { fmtDuration, fmtLocal } from './shared';

const ADGS_MAX_LABEL = 10;

function TicketLink({ ticket }: { ticket: AdgsQueueTicket }) {
  return (
    <a
      href={ticket.url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex shrink-0 items-center gap-1 font-mono text-xs text-zinc-800 hover:underline dark:text-zinc-200"
    >
      {ticket.identifier} <ExternalLink className="h-3 w-3 text-zinc-400" />
    </a>
  );
}

function WaitTime({ ticket }: { ticket: AdgsQueueTicket }) {
  if (ticket.secondsSinceGateTripped == null) return <span className="text-zinc-400">Unknown</span>;
  return (
    <span
      className={
        ticket.pickupBudgetBreached
          ? 'font-semibold text-red-700 dark:text-red-300'
          : 'text-zinc-600 dark:text-zinc-400'
      }
      title={ticket.gateTrippedAt ? `Gate tripped ${fmtLocal(ticket.gateTrippedAt)}` : undefined}
    >
      {fmtDuration(ticket.secondsSinceGateTripped)}
      {ticket.pickupBudgetBreached && ' — overdue'}
    </span>
  );
}

function ReadyTable({ tickets }: { tickets: AdgsQueueTicket[] }) {
  if (tickets.length === 0) {
    return (
      <div className="p-6 text-sm text-zinc-500">
        No deploy-gate ticket would be dispatched by a sweep starting now.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-zinc-50 text-left text-xs text-zinc-500 dark:bg-zinc-900">
          <tr>
            <th className="px-3 py-2 font-medium">Ticket</th>
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium">Gate wait</th>
            <th className="px-3 py-2 font-medium">State</th>
            <th className="px-3 py-2 font-medium">Project</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 bg-white dark:divide-zinc-800 dark:bg-zinc-950">
          {tickets.map((ticket) => (
            <tr
              key={ticket.identifier}
              className={
                ticket.pickupBudgetBreached
                  ? 'bg-red-50/70 dark:bg-red-950/20'
                  : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
              }
            >
              <td className="whitespace-nowrap px-3 py-2">
                <TicketLink ticket={ticket} />
              </td>
              <td className="px-3 py-2 text-xs text-zinc-700 dark:text-zinc-300">{ticket.title}</td>
              <td className="whitespace-nowrap px-3 py-2 text-xs">
                <WaitTime ticket={ticket} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-xs">{ticket.stateName}</td>
              <td className="px-3 py-2 text-xs text-zinc-500">{ticket.projectName ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReasonRows({ tickets }: { tickets: AdgsQueueTicket[] }) {
  return (
    <div className="divide-y divide-zinc-50 dark:divide-zinc-800">
      {tickets.map((ticket) => (
        <div key={ticket.identifier} className="flex items-center gap-3 px-3 py-1.5">
          <TicketLink ticket={ticket} />
          <span
            className="grow truncate text-xs text-zinc-600 dark:text-zinc-400"
            title={ticket.title}
          >
            {ticket.title}
          </span>
          <span className="shrink-0 text-[11px] text-zinc-500" title={ticket.reason ?? undefined}>
            {ticket.reason}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Same rows as `ReasonRows`, but the reason gets its own full-width line instead
 * of a truncated right-hand cell: a deploy-gate metadata conflict carries a
 * trailing parenthetical detail naming the exact contradiction, which is the
 * only actionable part of the string and the part `ReasonRows` cuts off.
 */
function ReasonBlock({ tickets }: { tickets: AdgsQueueTicket[] }) {
  return (
    <div className="divide-y divide-zinc-50 dark:divide-zinc-800">
      {tickets.map((ticket) => (
        <div key={ticket.identifier} className="px-3 py-2">
          <div className="flex items-center gap-3">
            <TicketLink ticket={ticket} />
            <span className="grow truncate text-xs text-zinc-700 dark:text-zinc-300">
              {ticket.title}
            </span>
            <span className="shrink-0 text-[11px] text-zinc-500">{ticket.stateName}</span>
          </div>
          <div className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">{ticket.reason}</div>
        </div>
      ))}
    </div>
  );
}

export function AdgsTicketsTab() {
  const queue = useQuery({
    queryKey: ['adgs-queue'],
    queryFn: () => api.getAdgsQueue(),
    refetchInterval: 60_000,
  });

  if (queue.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading deploy gates from Linear…</div>;
  }
  if (queue.isError) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300">
        ADGS queue failed to load. Check Worker logs for /admin/autobuild/adgs/queue; a 503 names
        the missing Linear or GitHub credential.
      </div>
    );
  }

  const {
    wouldDispatchNow = [],
    beyondCap = [],
    gateNotSatisfied = [],
    malformed = [],
    suppressed = [],
    notInScope = [],
    pickupBudgetHours = 6,
    generatedAt,
  } = queue.data ?? {};

  // Every bucket, spread once: the overdue banner is derived from this array so a
  // bucket added later cannot silently fall out of the breach count.
  const allTickets = [
    ...wouldDispatchNow,
    ...beyondCap,
    ...gateNotSatisfied,
    ...malformed,
    ...suppressed,
    ...notInScope,
  ];
  const overdue = allTickets.filter((ticket) => ticket.pickupBudgetBreached);

  return (
    <div className="space-y-4">
      {overdue.length > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-semibold">
              The deploy-trigger-pickup assertion&apos;s budget was breached.
            </span>{' '}
            {overdue.map((ticket) => ticket.identifier).join(', ')} have remained dispatchable for
            more than {pickupBudgetHours} hours.
          </div>
        </div>
      )}

      <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <div className="border-b border-zinc-100 px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 dark:border-zinc-800">
          Would dispatch now ({wouldDispatchNow.length}) — oldest gate first
        </div>
        <ReadyTable tickets={wouldDispatchNow} />
      </div>

      {beyondCap.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-white dark:border-amber-800 dark:bg-zinc-900">
          <div className="border-b border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] uppercase tracking-wide text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
            Beyond the {ADGS_MAX_LABEL}-ticket cap ({beyondCap.length}) — next sweep takes these
          </div>
          <ReadyTable tickets={beyondCap} />
        </div>
      )}

      <div className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <div className="border-b border-zinc-100 px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 dark:border-zinc-800">
          Gate not satisfied ({gateNotSatisfied.length})
        </div>
        {gateNotSatisfied.length > 0 ? (
          <ReasonRows tickets={gateNotSatisfied} />
        ) : (
          <div className="p-6 text-sm text-zinc-500">No deploy gates are currently waiting.</div>
        )}
      </div>

      {/* Always expanded: ADGS holds these and no deploy releases them, so a
          collapse would hide the only surface that reports them. */}
      <div className="rounded-lg border border-amber-300 bg-white dark:border-amber-800 dark:bg-zinc-900">
        <div className="flex items-center gap-1 border-b border-amber-200 bg-amber-50/70 px-3 py-2 text-[11px] uppercase tracking-wide text-amber-700 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
          <AlertTriangle className="h-3 w-3" />
          Malformed or conflicting filings ({malformed.length}) — ADGS holds these
        </div>
        {malformed.length > 0 ? (
          <ReasonBlock tickets={malformed} />
        ) : (
          <div className="p-6 text-sm text-zinc-500">
            No malformed or conflicting deploy-gate filings.
          </div>
        )}
      </div>

      <details className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <summary className="cursor-pointer px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
          Suppressed — claimed or time-gated ({suppressed.length})
        </summary>
        <div className="border-t border-zinc-100 dark:border-zinc-800">
          {suppressed.length > 0 ? (
            <ReasonRows tickets={suppressed} />
          ) : (
            <div className="p-6 text-sm text-zinc-500">
              Nothing is currently claimed or time-gated.
            </div>
          )}
        </div>
      </details>

      <details className="rounded-lg border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
        <summary className="cursor-pointer px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
          Other exclusions ({notInScope.length})
        </summary>
        <div className="border-t border-zinc-100 dark:border-zinc-800">
          {notInScope.length > 0 ? (
            <ReasonRows tickets={notInScope} />
          ) : (
            <div className="p-6 text-sm text-zinc-500">No other exclusions.</div>
          )}
        </div>
      </details>

      <div className="text-[11px] text-zinc-400">
        Dispatch, cap, gate, and exclusion decisions come from the same selector used by adgs.yml.
        Completed, canceled and duplicate tickets are not fetched at all, so a finished ticket is
        never shown as a malformed filing. Malformed shapes and deploy-gate metadata conflicts
        are shown expanded because ADGS holds them and no deploy will release them. Gate age begins
        when the last blocker (or parent fallback) reaches the required deploy state; the
        deploy-trigger-pickup assertion flags an otherwise-dispatchable ticket after{' '}
        {pickupBudgetHours} hours.
        {generatedAt ? ` Snapshot ${fmtLocal(generatedAt)}, refreshes every 60s.` : ''}
      </div>
    </div>
  );
}
